import {
  ArrowsClockwise,
  BookOpenText,
  Exam,
  FileText,
  Flask,
  VideoCamera,
  type Icon,
} from '@phosphor-icons/react';

import type { LectureNodeKind } from '@/types/Lecture';

/** A right panel a row opens in place of the study preview. */
export type LectureNodePanelKey = 'session' | 'case';

/** What a plan step counts, in the order its card names them. */
export type LecturePlanCountPart = 'videos' | 'summary' | 'questions' | 'cards';

export type LecturePlanStepContent =
  /** Fixed copy, for a part with nothing to count. */
  | { text: string }
  /** Counts from the child's preview; the summary only if it has one. */
  | { order: readonly LecturePlanCountPart[]; summaryLabel: string };

export interface LectureNodeKindSpec {
  /** Beside the name, in the tree and in the title of its panels. */
  pill?: string;
  /** In the tree, instead of the stored full name. */
  shortLabel?: string;
  /** The row's icon. Folders always keep the folder icon. */
  icon?: Icon;
  panel?: LectureNodePanelKey;
  /** Selecting this folder shows its children as plan steps. */
  hasPlan?: boolean;
  /** What the chat input calls this part, in place of "lecture". */
  chatNoun?: string;
  /** How this part's step card reads in its folder's plan. */
  planStep?: LecturePlanStepContent;
}

/**
 * The one place a lecture node kind maps to its UI. A new kind is one entry
 * here, plus its panel in `LectureNodePanel.tsx` if it opens one.
 */
export const LECTURE_NODE_KINDS: Record<LectureNodeKind, LectureNodeKindSpec> =
  {
    LECTURE: { pill: 'Lecture', hasPlan: true },
    CBL: { pill: 'CBL', hasPlan: true },
    LAB: { pill: 'Lab', icon: Flask },
    REVIEW: { pill: 'Review', icon: ArrowsClockwise },
    PRE_WORK: {
      shortLabel: 'Pre-Work',
      icon: BookOpenText,
      planStep: {
        order: ['videos', 'summary', 'questions', 'cards'],
        summaryLabel: 'reading',
      },
    },
    POST_WORK: {
      shortLabel: 'Post-Work',
      icon: Exam,
      planStep: {
        order: ['videos', 'questions', 'cards', 'summary'],
        summaryLabel: '1 summary',
      },
    },
    SESSION_VIDEO: {
      shortLabel: 'Session',
      icon: VideoCamera,
      panel: 'session',
      planStep: { text: 'Lecture recording' },
    },
    CASE_DOCUMENT: {
      shortLabel: 'Case',
      icon: FileText,
      panel: 'case',
      chatNoun: 'case',
      planStep: { text: 'Case document' },
    },
  };

/**
 * The kind's entry. Undefined for no kind, and for a kind this build does not
 * know yet, so that row renders as a plain one.
 */
export const lectureNodeKindSpec = (
  kind: LectureNodeKind | null | undefined
): LectureNodeKindSpec | undefined =>
  kind ? LECTURE_NODE_KINDS[kind] : undefined;
